<script setup>
import { ref, onMounted } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';
import { adminAPI } from '../service/api.js';
import NewApiClientDialog from './NewApiClientDialog.vue';
import RotateApiClientDialog from './RotateApiClientDialog.vue';

const toast = useToast();
const confirm = useConfirm();

const clients = ref([]);
const loading = ref(false);
const newDialogOpen = ref(false);
const rotateDialogOpen = ref(false);
const rotateTarget = ref(null);

const fetch = async () => {
  loading.value = true;
  try {
    const response = await adminAPI.listApiClients();
    clients.value = response.data || [];
  } catch (e) {
    console.error('listApiClients failed:', e);
    toast.add({
      severity: 'error',
      summary: 'Failed to load API clients',
      detail: e?.response?.data || e?.message || 'Unknown error',
      life: 5000,
    });
  } finally {
    loading.value = false;
  }
};

onMounted(fetch);

const openNew = () => { newDialogOpen.value = true; };

const askRotate = (client) => {
  rotateTarget.value = client;
  rotateDialogOpen.value = true;
};

const askDeactivate = (client) => {
  confirm.require({
    message: `Deactivate ${client.client_id}? This is terminal — to restore access for ${client.tenant}, you'll need to issue a new client.`,
    header: 'Deactivate API client',
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Deactivate',
    acceptClass: 'p-button-danger',
    accept: () => doDeactivate(client),
  });
};

const doDeactivate = async (client) => {
  try {
    await adminAPI.deactivateApiClient(client.client_id);
    toast.add({
      severity: 'success',
      summary: 'Client deactivated',
      detail: client.client_id,
      life: 3000,
    });
    await fetch();
  } catch (e) {
    console.error('deactivateApiClient failed:', e);
    toast.add({
      severity: 'error',
      summary: 'Failed to deactivate',
      detail: e?.response?.data || e?.message || 'Unknown error',
      life: 5000,
    });
  }
};

const fmtDate = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString();
  } catch (e) {
    return s;
  }
};

const fmtNullable = (s) => s || '—';
</script>

<template>
  <div class="admin-view">
    <ConfirmDialog />

    <div class="view-header">
      <h2>API clients</h2>
      <Button label="New API client" icon="pi pi-plus" @click="openNew" />
    </div>

    <DataTable
      :value="clients"
      :loading="loading"
      data-key="client_id"
      striped-rows
      size="small"
      class="clients-table"
    >
      <template #empty>
        <div class="empty">No API clients exist yet. Click "New API client" to create one.</div>
      </template>

      <Column field="tenant" header="Tenant" />
      <Column field="client_id" header="Client ID">
        <template #body="{ data }">
          <span class="mono">{{ data.client_id }}</span>
        </template>
      </Column>
      <Column field="description" header="Description">
        <template #body="{ data }">{{ fmtNullable(data.description) }}</template>
      </Column>
      <Column field="active" header="Status">
        <template #body="{ data }">
          <Tag
            :value="data.active ? 'active' : 'inactive'"
            :severity="data.active ? 'success' : 'secondary'"
          />
        </template>
      </Column>
      <Column header="Created">
        <template #body="{ data }">
          <div>{{ fmtDate(data.created_at) }}</div>
          <small class="meta">by {{ fmtNullable(data.created_by) }}</small>
        </template>
      </Column>
      <Column header="Last used">
        <template #body="{ data }">{{ fmtDate(data.last_used_at) }}</template>
      </Column>
      <Column header="Deactivated">
        <template #body="{ data }">
          <template v-if="data.deactivated_at">
            <div>{{ fmtDate(data.deactivated_at) }}</div>
            <small class="meta">by {{ fmtNullable(data.deactivated_by) }}</small>
          </template>
          <template v-else>—</template>
        </template>
      </Column>
      <Column header="Actions">
        <template #body="{ data }">
          <div class="row-actions">
            <Button
              icon="pi pi-refresh"
              label="Rotate"
              severity="warn"
              size="small"
              :disabled="!data.active"
              @click="askRotate(data)"
            />
            <Button
              icon="pi pi-ban"
              label="Deactivate"
              severity="danger"
              size="small"
              :disabled="!data.active"
              @click="askDeactivate(data)"
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <NewApiClientDialog
      v-model:visible="newDialogOpen"
      @created="fetch"
    />
    <RotateApiClientDialog
      v-model:visible="rotateDialogOpen"
      :client="rotateTarget"
      @rotated="fetch"
    />
  </div>
</template>

<style scoped>
.admin-view {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.view-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.view-header h2 {
  margin: 0;
}
.clients-table {
  background: #fff;
}
.mono {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.85rem;
}
.meta {
  color: #777;
}
.row-actions {
  display: flex;
  gap: 0.5rem;
}
.empty {
  padding: 2rem;
  color: #888;
}
</style>
